/** venture/DocFieldModal.tsx — 서류 미리보기를 보며 값을 적는 모달 (마스터 DocViewModal 과 같은 문법, 2026-09-24)
 *  위: 서류(PDF/이미지 · 확대) · 아래: 지금 적는 항목 입력칸 · 같은 서류에서 읽는 다른 항목으로 ← → 이동
 *  13단계 VICS 화면에서 📄 등기부등본/사업자등록증/벤처기업확인서 버튼과 첨부 「보기」가 이 모달을 연다. */
import React, { useEffect, useRef, useState } from 'react';
import Icon from './icons';
import { fmtComma, onlyDigits } from './bondcalc';

export interface ModalField { key: string; label: string; value: string; kind?: 'text' | 'date' | 'num'; hint?: string; onChange: (v: string) => void; }
interface Props {
  title: string;                       // 예: '(주)코텍 · 등기부등본'
  fileName: string;                    // 비어 있으면 미업로드 안내
  load?: () => Promise<string>;        // 미리보기 URL (서명 URL 은 매번 새로)
  noneMsg?: string;
  fields: ModalField[];                // 이 서류에서 읽는 항목들
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}

export default function DocFieldModal({ title, fileName, load, noneMsg, fields, index, onIndex, onClose }: Props) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  // 미리보기는 파일이 바뀔 때만 다시 불러온다 — load 함수는 부모가 렌더마다 새로 만들므로 ref 로 받는다 (타이핑마다 재로딩 방지)
  const loadRef = useRef(load); loadRef.current = load;
  useEffect(() => {
    setUrl(''); setZoom(1);
    const fn = loadRef.current;
    if (!fileName || !fn) { setLoading(false); return; }
    let alive = true; setLoading(true);
    fn().then((u) => { if (alive) { setUrl(u || ''); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fileName, title]);

  const f = fields[index] || fields[0];
  const isImg = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(fileName || '');
  const wide = !!url;
  const zbtn: React.CSSProperties = { background: '#fff', border: '1px solid #c5cbd6', borderRadius: 6, padding: '2px 9px', cursor: 'pointer', fontSize: 13, lineHeight: 1.4, color: '#1a2438' };
  const isNum = f && f.kind === 'num';
  const shown = f ? (isNum ? fmtComma(f.value) : f.value) : '';

  return (
    <div className="ovm">
      <div className="ovbox docbox" style={wide ? { maxWidth: 1040, width: '94%' } : undefined}>
        <div className="ovh"><b>📄 {title} 미리보기</b><button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button></div>
        <div className="docprev">
          {fileName ? (
            url ? (
              isImg ? (
                <div style={{ position: 'relative', width: '100%', height: '56vh', overflow: 'auto', background: '#525659', borderRadius: 8 }}>
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 2 }}>
                    <button type="button" onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))} style={zbtn}>－</button>
                    <button type="button" onClick={() => setZoom(1)} style={zbtn}>{Math.round(zoom * 100)}%</button>
                    <button type="button" onClick={() => setZoom((z) => Math.min(5, Math.round((z + 0.25) * 100) / 100))} style={zbtn}>＋</button>
                  </div>
                  <div style={{ minHeight: '100%', display: 'flex', alignItems: zoom > 1 ? 'flex-start' : 'center', justifyContent: zoom > 1 ? 'flex-start' : 'center', padding: 8, boxSizing: 'border-box' }}>
                    <img src={url} alt={fileName} onClick={() => setZoom((z) => (z > 1 ? 1 : 2))} style={{ width: zoom === 1 ? 'auto' : (zoom * 100) + '%', maxWidth: zoom === 1 ? '100%' : 'none', maxHeight: zoom === 1 ? '100%' : 'none', objectFit: 'contain', cursor: zoom > 1 ? 'zoom-out' : 'zoom-in' }} />
                  </div>
                </div>
              ) : (
                <iframe title={fileName} src={url} style={{ width: '100%', height: '56vh', border: '1px solid #e5e9f2', borderRadius: 8, background: '#525659' }} />
              )
            ) : (
              <div className="docimg">📄<div className="docfn">{fileName}</div><div className="dochint">{loading ? '미리보기 불러오는 중…' : '미리보기를 불러올 수 없습니다'}</div></div>
            )
          ) : (
            <div className="docnone">{noneMsg || '파일이 없습니다 — 먼저 업로드해 주세요'}</div>
          )}
        </div>
        {f ? (
          <div className="docfield">
            <label>{f.label} 입력 (미리보기 보며){fields.length > 1 ? <span className="s3muted" style={{ fontWeight: 500, marginLeft: 6 }}>{index + 1}/{fields.length}</span> : null}</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {fields.length > 1 ? <button type="button" className="rbtn" disabled={index <= 0} onClick={() => onIndex(index - 1)} title="이 서류에서 읽는 이전 항목">←</button> : null}
              <input style={{ flex: 1 }} type={f.kind === 'date' ? 'date' : 'text'} inputMode={isNum ? 'numeric' : undefined} value={shown} placeholder={f.hint || f.label} autoFocus
                onChange={(e) => f.onChange(isNum ? onlyDigits(e.target.value) : e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && index < fields.length - 1) onIndex(index + 1); }} />
              {fields.length > 1 ? <button type="button" className="rbtn" disabled={index >= fields.length - 1} onClick={() => onIndex(index + 1)} title="이 서류에서 읽는 다음 항목 (Enter)">→</button> : null}
            </div>
          </div>
        ) : null}
        <div className="docnote">{fields.length > 1 ? 'Enter 또는 → 로 같은 서류의 다음 항목 · ' : ''}입력은 바로 저장됩니다 · Esc 또는 [닫기]</div>
      </div>
    </div>
  );
}
